import React, { useState, useEffect } from 'react';
import { Topic, subscribeToUserTopics, updateTopicStatus, updateTopicType, unsubscribeFromTopic } from '../../services/topicService';
import TopicItem from '../topic-item';
import FlipMove from 'react-flip-move';
import './TopicList.css';

interface TopicListProps {
  user: any; // User object needed for subscription functionality
  onTopicClick: (topic: Topic) => void;
}

const TopicList: React.FC<TopicListProps> = ({ user, onTopicClick }) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleStatusToggle = async (topicId: string, newStatus: 'ACTIVE' | 'PAUSED') => {
    try {
      await updateTopicStatus(topicId, newStatus);
      // The real-time subscription will update the UI automatically
    } catch (error) {
      console.error('Error updating topic status:', error);
      setError('Failed to update topic status. Please try again.');
    }
  };

  const handleTypeToggle = async (topicId: string, newType: 'USER' | 'COMMON') => {
    try {
      await updateTopicType(topicId, newType);
      // The real-time subscription will update the UI automatically
    } catch (error) {
      console.error('Error updating topic type:', error);
      setError('Failed to update topic type. Please try again.');
    }
  };

  const handleDelete = async (topicId: string) => {
    try {
      // Immediately remove the topic from the local state (optimistic update)
      setTopics(prevTopics => prevTopics.filter(topic => topic.id !== topicId));
      
      // Send the unsubscribe request to the backend
      await unsubscribeFromTopic(topicId, user.id);
    } catch (error) {
      console.error('Error unsubscribing from topic:', error);
      setError('Failed to unsubscribe from topic. Please try again.');
      
      // If the API call fails, the real-time subscription will restore the correct state
      // by re-adding the topic that we optimistically removed
    }
  };

  useEffect(() => {
    if (!user || !user.id) {
      setTopics([]);
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToUserTopics(user.id, (newTopics: Topic[]) => {
      setTopics(newTopics);
      setLoading(false);
      setError(null);
    });

    // Error handling for the subscription
    const handleError = (error: Error) => {
      console.error('Error subscribing to user topics:', error);
      setError('Failed to load topics. Please refresh the page.');
      setLoading(false);
    };

    // Clean up subscription on unmount
    return () => {
      try {
        if (unsubscribe) {
          unsubscribe();
        }
      } catch (error) {
        handleError(error as Error);
      }
    };
  }, [user]);

  if (loading) {
    return (
      <div className="topic-list">
        <div className="loading">Loading topics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="topic-list">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="topic-list">
      {topics.length === 0 ? (
        <div className="empty-state">
          <p>No topics yet. Add your first topic above!</p>
        </div>
      ) : (
        <div className="topics-container">
          <FlipMove
            duration={300}
            easing="ease-out"
            appearAnimation="fade"
            enterAnimation="fade"
            leaveAnimation="fade"
          >
            {topics.map((topic) => (
              <div key={topic.id}>
                <TopicItem 
                  topic={topic} 
                  user={user}
                  onTopicClick={onTopicClick} 
                  onStatusToggle={handleStatusToggle}
                  onTypeToggle={handleTypeToggle}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </FlipMove>
        </div>
      )}
    </div>
  );
};

export default TopicList;